import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../authConfig';
import {
  Box,
  Container,
  Typography,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Alert,
  AlertTitle,
  Paper,
  Button
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

interface MarketplaceOnboardingState {
  currentStep: number;
  error: string | null;
  subscriptionDetails: any | null;
  actualApiBaseUrl: string | null;
}

const steps = [
  'Verifying Purchase',
  'Creating Account',
  'Activating Subscription',
  'Setup Complete'
];

const UNIVERSAL_MARKETPLACE_API_BASE = import.meta.env.VITE_UNIVERSAL_MARKETPLACE_API_BASE || '/api';

const normalizeApiBase = (baseUrl: string): string => {
  const trimmed = baseUrl.replace(/\/+$/, '');
  return trimmed.endsWith('/api') ? trimmed : `${trimmed}/api`;
};

const resolvePreferredOnboardingApiBase = (searchParams: URLSearchParams): string | null => {
  const resolvedApi = (searchParams.get('resolvedApi') ?? '').trim();
  if (!resolvedApi) {
    return null;
  }

  try {
    return normalizeApiBase(resolvedApi);
  } catch {
    return null;
  }
};

const buildOnboardingUrl = (baseUrl: string, path: string): string => {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${normalizeApiBase(baseUrl)}${normalizedPath}`;
};

const parseErrorMessage = async (response: Response, fallback: string): Promise<string> => {
  try {
    const responseText = await response.text();
    if (!responseText) {
      return fallback;
    }

    try {
      const parsed = JSON.parse(responseText);
      if (parsed && typeof parsed.error === 'string' && parsed.error.length > 0) {
        return parsed.error;
      }
      if (parsed && typeof parsed.message === 'string' && parsed.message.length > 0) {
        return parsed.message;
      }
      if (parsed && typeof parsed.code === 'string' && parsed.code.length > 0) {
        return parsed.code;
      }
      return fallback;
    } catch {
      const lowered = responseText.toLowerCase();
      if (lowered.includes('service is unavailable')) {
        return 'The onboarding service is temporarily unavailable, or your marketplace token has expired. Please relaunch onboarding from Azure Marketplace and try again.';
      }
      if (lowered.includes('expired')) {
        return 'Your marketplace onboarding link has expired. Relaunch onboarding from Azure Marketplace to get a fresh token.';
      }
      return responseText;
    }
  } catch {
    return fallback;
  }
};

const MarketplaceLanding: React.FC = () => {
  const [searchParams] = useSearchParams();
    const { instance } = useMsal();
  const onboardingTokenRef = useRef<string | null>(null);
  const [state, setState] = useState<MarketplaceOnboardingState>({
    currentStep: 0,
    error: null,
    subscriptionDetails: null,
    actualApiBaseUrl: null // Initialized to null
  });

  useEffect(() => {
    const token = searchParams.get('token');
    
    if (!token) {
      setState(prev => ({
        ...prev,
        error: 'No marketplace token provided. Please return to Azure Marketplace and try again.'
      }));
      return;
    }

    // React StrictMode runs effects twice in development. Ensure onboarding starts once per token.
    if (onboardingTokenRef.current === token) {
      return;
    }
    onboardingTokenRef.current = token;

    handleMarketplaceOnboarding(token);
  }, [searchParams]);

  const handleMarketplaceOnboarding = async (token: string) => {
    try {
      // Step 1: Start session-based onboarding
      setState(prev => ({ ...prev, currentStep: 0 }));

      const functionKey = import.meta.env.VITE_FUNCTION_KEY;
      const sharedHeaders: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (functionKey) {
        sharedHeaders['x-functions-key'] = functionKey;
      }

      // Always resolve first so we can enforce correct portal routing even when
      // a resolvedApi query parameter is already present.
      const resolveResponse = await fetch(
        buildOnboardingUrl(UNIVERSAL_MARKETPLACE_API_BASE, '/marketplace/onboarding/resolve'),
        {
          method: 'POST',
          headers: sharedHeaders,
          body: JSON.stringify({ token })
        }
      );

      if (!resolveResponse.ok) {
        const message = await parseErrorMessage(resolveResponse, 'Failed to resolve environment');
        throw new Error(message);
      }

      const discovery = await resolveResponse.json();
      const actualApiBaseUrl = discovery.actualApiBaseUrl;
      const targetPortalUrl: string | undefined = discovery.targetPortalUrl;

      // If backend indicates a different portal, redirect before starting onboarding.
      if (targetPortalUrl) {
        try {
          const targetOrigin = new URL(targetPortalUrl).origin;
          if (targetOrigin !== window.location.origin) {
            const destination = new URL(`${targetPortalUrl}/marketplace/landing`);
            destination.searchParams.set('token', token);
            if (actualApiBaseUrl) {
              destination.searchParams.set('resolvedApi', actualApiBaseUrl);
            }
            window.location.href = destination.toString();
            return; // Browser navigates away — stop processing here
          }
        } catch {
          // Malformed targetPortalUrl — fall through and continue on this portal
        }
      }

      // Choose one API base and keep all calls on the same backend path.
      const preferredOnboardingApiBase = resolvePreferredOnboardingApiBase(searchParams);
      let onboardingApiBase = preferredOnboardingApiBase;

      if (!onboardingApiBase) {
        if (!actualApiBaseUrl) {
          throw new Error('Backend did not provide the actual API base URL.');
        }
        onboardingApiBase = normalizeApiBase(actualApiBaseUrl);
      }

      // Step 2: Initialize the stateful session on the correct environment
      const startResponse = await fetch(buildOnboardingUrl(onboardingApiBase, '/marketplace/onboarding/start'), {
        method: 'POST',
        headers: sharedHeaders,
        body: JSON.stringify({ token })
      });

      if (!startResponse.ok) {
        const message = await parseErrorMessage(startResponse, 'Failed to initialize onboarding session');
        throw new Error(message);
      }

      const session = await startResponse.json();
      const subscriptionData = session.subscription;
      const sessionId = session.sessionId;
      let currentVersion = session.version;

      setState(prev => ({
        ...prev,
        currentStep: 1,
        subscriptionDetails: subscriptionData,
        actualApiBaseUrl: onboardingApiBase // Store normalized API base URL
      }));

      // Step 2: Advance subscription (activate)
      await new Promise(resolve => setTimeout(resolve, 1000)); // Brief pause for UX

      const advanceResponse = await fetch(
        buildOnboardingUrl(onboardingApiBase, `/marketplace/onboarding/${encodeURIComponent(sessionId)}/advance`),
        {
          method: 'POST',
          headers: sharedHeaders,
          body: JSON.stringify({
            command: 'confirm_activation',
            version: currentVersion,
            payload: null
          })
        }
      );

      if (!advanceResponse.ok) {
        const message = await parseErrorMessage(advanceResponse, 'Failed to activate subscription');
        throw new Error(message);
      }

      const advanceSession = await advanceResponse.json();
      currentVersion = advanceSession.version;

      setState(prev => ({ ...prev, currentStep: 2 }));

      // Step 3: Handle consent if required
      if (advanceSession.nextAction === 'open_consent_window' && advanceSession.consent?.requirements?.length > 0) {
        console.log('Consent requirements:', advanceSession.consent.requirements);
      }

      // Step 4: Verify consent
      await new Promise(resolve => setTimeout(resolve, 1000));

      const verifyResponse = await fetch(
        buildOnboardingUrl(onboardingApiBase, `/marketplace/onboarding/${encodeURIComponent(sessionId)}/consent/verify`),
        {
          method: 'POST',
          headers: sharedHeaders,
          body: JSON.stringify({ version: currentVersion })
        }
      );

      if (!verifyResponse.ok) {
        const message = await parseErrorMessage(verifyResponse, 'Failed to verify purchase');
        throw new Error(message);
      }

      setState(prev => ({ ...prev, currentStep: 3 }));

      // Step 5: Determine redirect based on plan (unchanged logic)
      await new Promise(resolve => setTimeout(resolve, 2000));

      const isMspPlan = subscriptionData.planId === 'msp-client' || subscriptionData.planId === 'msp-client-dev';

      // In session-based flow, successful verification implies account activation
      // so treat all paths as first-time onboarding setup flow
      const redirectPath = isMspPlan
        ? `/certificate-wizard?plan=${encodeURIComponent(subscriptionData.planId)}`
        : `/certificate-wizard?plan=${encodeURIComponent(subscriptionData.planId)}&source=marketplace`;

      const redirectStartPage = `${window.location.origin}${redirectPath}${redirectPath.includes('?') ? '&' : '?'}resolvedApi=${encodeURIComponent(onboardingApiBase)}`;

      // Determine redirect destination, then sign the user in via MSAL.
      // Navigating directly (navigate()) would land on the sign-in screen because the
      // user is unauthenticated at this point.  Using loginRedirect() with
      // redirectStartPage mirrors the MSP portal post-onboard sign-in pattern.
      await instance.loginRedirect({
        ...loginRequest,
        redirectStartPage,
      });

    } catch (error) {
      console.error('Marketplace onboarding error:', error);
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'An unexpected error occurred'
      }));
    }
  };

  const handleRetry = () => {
    const token = searchParams.get('token');
    if (token) {
      onboardingTokenRef.current = null;
      setState({
        currentStep: 0,
        error: null,
        subscriptionDetails: null,
        actualApiBaseUrl: null // Reset actualApiBaseUrl on retry
      });
      handleMarketplaceOnboarding(token);
    }
  };

  const handleContactSupport = () => {
    window.location.href = 'mailto:support@tc-config.com?subject=Marketplace Onboarding Issue';
  };

  if (state.error) {
    return (
      <Container maxWidth="md">
        <Box sx={{ mt: 8, mb: 4 }}>
          <Paper elevation={3} sx={{ p: 4 }}>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <ErrorOutlineIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
              <Typography variant="h4" gutterBottom>
                Onboarding Failed
              </Typography>
            </Box>

            <Alert severity="error" sx={{ mb: 3 }}>
              <AlertTitle>Error</AlertTitle>
              {state.error}
            </Alert>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button variant="contained" onClick={handleRetry}>
                Try Again
              </Button>
              <Button variant="outlined" onClick={handleContactSupport}>
                Contact Support
              </Button>
            </Box>
          </Paper>
        </Box>
      </Container>
    );
  }

  if (state.currentStep === 3) {
    return (
      <Container maxWidth="md">
        <Box sx={{ mt: 8, mb: 4 }}>
          <Paper elevation={3} sx={{ p: 4 }}>
            <Box sx={{ textAlign: 'center' }}>
              <CheckCircleOutlineIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
              <Typography variant="h4" gutterBottom>
                Welcome to TC-Config!
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Your account has been successfully created and activated.
              </Typography>
              
              {state.subscriptionDetails && (
                <Box sx={{ mb: 3, textAlign: 'left', bgcolor: 'background.default', p: 2, borderRadius: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Subscription Details:
                  </Typography>
                  <Typography variant="body2">
                    <strong>Plan:</strong> {state.subscriptionDetails.planId}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Quantity:</strong> {state.subscriptionDetails.quantity} seat{state.subscriptionDetails.quantity > 1 ? 's' : ''}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Email:</strong> {state.subscriptionDetails.email}
                  </Typography>
                </Box>
              )}

              <Typography variant="body2" color="text.secondary">
                Redirecting you to complete your setup...
              </Typography>
              <CircularProgress size={24} sx={{ mt: 2 }} />
            </Box>
          </Paper>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ mt: 8, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h4" align="center" gutterBottom>
            Setting Up Your Account
          </Typography>
          <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 4 }}>
            Please wait while we complete your onboarding...
          </Typography>

          <Stepper activeStep={state.currentStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column' }}>
            <CircularProgress size={40} sx={{ mb: 2 }} />
            <Typography variant="body2" color="text.secondary">
              {steps[state.currentStep]}...
            </Typography>
          </Box>

          {state.subscriptionDetails && (
            <Box sx={{ mt: 4, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Subscription Information:
              </Typography>
              <Typography variant="body2">
                <strong>Plan:</strong> {state.subscriptionDetails.planId} | <strong>Seats:</strong> {state.subscriptionDetails.quantity}
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default MarketplaceLanding;
